// src/pages/ResourcesPage.js
// Premium Resources hub page

import React from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';

// Layout components
import SmoothScroll from '../components/SmoothScroll';
import Navbar from '../components/Navbar';
import SEO from '../components/SEO';

// Resource components
import ResourcesHero from '../components/resources/ResourcesHero';
import ResourcesControls from '../components/resources/ResourcesControls';
import ResourcesGrid from '../components/resources/ResourcesGrid';
import ResourcesSkeleton from '../components/resources/ResourcesSkeleton';
import ResourcesEmptyState from '../components/resources/ResourcesEmptyState';

// Hooks
import { useResourcesList } from '../hooks/useContentful';
import { useResourceFilters } from '../hooks/useResourceFilters';

// Styles
import './ResourcesPage.css';

const ResourcesPage = () => {
  const { scrollYProgress } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001,
  });

  // Fetch resources from Contentful
  const { data, loading, error } = useResourcesList();

  // Filter and search state
  const {
    filters,
    setSearch,
    setContentType,
    setSortOrder,
    toggleTopic,
    clearFilters,
    filteredData,
    hasActiveFilters,
    countsByType,
    totalCount,
    filteredCount,
  } = useResourceFilters(data?.resources || []);

  return (
    <SmoothScroll>
      {/* SEO */}
      <SEO
        title="Resources"
        description="Explore articles, case studies, and insights on AI, GenAI, manufacturing analytics, and enterprise digital transformation from Rosalytics."
        canonical="/resources"
      />

      {/* Progress Bar */}
      <motion.div
        className="resources-page__progress"
        style={{ scaleX }}
      />

      <Navbar />

      <main className="resources-page">
        {/* Hero Section */}
        <ResourcesHero
          activeTopics={filters.activeTopics}
          onTopicClick={toggleTopic}
        />

        {/* Controls Bar */}
        <ResourcesControls
          search={filters.search}
          onSearchChange={setSearch}
          contentType={filters.contentType}
          onContentTypeChange={setContentType}
          sortOrder={filters.sortOrder}
          onSortChange={setSortOrder}
          filteredCount={filteredCount}
          totalCount={totalCount}
          countsByType={countsByType}
          hasActiveFilters={hasActiveFilters}
          onClearFilters={clearFilters}
        />

        {/* Content Area */}
        {loading ? (
          <ResourcesSkeleton count={6} />
        ) : error ? (
          <div className="resources-page__error">
            <p>Unable to load resources. Please try again later.</p>
          </div>
        ) : filteredCount === 0 ? (
          <ResourcesEmptyState onClearFilters={clearFilters} />
        ) : (
          <ResourcesGrid resources={filteredData} />
        )}

        {/* Footer */}
        <footer className="resources-page__footer">
          <div className="resources-page__footer-container">
            <div className="resources-page__footer-grid">
              <div>
                <h3 className="resources-page__footer-brand">Rosalytics</h3>
                <p className="resources-page__footer-tagline">
                  Operational Intelligence for the Modern Enterprise
                </p>
              </div>

              <div>
                <h4 className="resources-page__footer-heading">Capabilities</h4>
                <ul className="resources-page__footer-list">
                  <li>GenAI Solutions</li>
                  <li>Manufacturing Analytics</li>
                  <li>Custom Applications</li>
                </ul>
              </div>

              <div>
                <h4 className="resources-page__footer-heading">Quick Links</h4>
                <ul className="resources-page__footer-list">
                  <li><a href="/#capabilities">Our Approach</a></li>
                  <li><a href="/#demos">Live Demos</a></li>
                  <li><a href="/resources">Resources</a></li>
                  <li><a href="/#team">Team</a></li>
                  <li><a href="/#contact">Contact</a></li>
                </ul>
              </div>

              <div>
                <h4 className="resources-page__footer-heading">Contact</h4>
                <ul className="resources-page__footer-list">
                  <li>Milwaukee, WI</li>
                  <li>United States</li>
                  <li className="resources-page__footer-email">info@rosalytics.com</li>
                </ul>
              </div>
            </div>

            <div className="resources-page__footer-bottom">
              <p>© 2025 Rosalytics. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </main>
    </SmoothScroll>
  );
};

export default ResourcesPage;
